import { CalendarProps } from "PFComponents/calendar/calendar";
import { InlineRangeCalendar } from "PFComponents/calendar/inline_calendar";
import { Option } from "PFComponents/calendar/range_calendar/options";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { FilterWithDefaults, PeriodRange } from "PFTypes";
import { CSSProperties, RefObject, useMemo } from "react";

import { letFilterRestore } from "../../use_filtered_collection";

type DateRangeItemProps = {
  handleChange: (value: PeriodRange | null) => void;
  filter: FilterWithDefaults;
  error?: string;
  warning?: string;
  displayMonth?: CSSProperties;
  portalRef?: RefObject<HTMLDivElement>;
  disabled?: boolean;
  maxDaysLength?: number;
  weekStartDay?: CalendarProps["weekStartDay"];
};

export const DateRangeItem = ({ filter, handleChange, ...inlineRangeCalendarProps }: DateRangeItemProps) => {
  const { formatISODate } = useDateFormatter();
  const {
    value,
    required,
    minDate,
    options = []
  } = filter as FilterWithDefaults & {
    minDate?: string;
    options?: Option[];
  };
  const { start, end, period } = (value || {}) as PeriodRange;

  const onHandleChange = (start, end, selectedPeriod) => {
    if (!start && !end && !selectedPeriod) {
      handleChange(null);
    }
    if (start && end) {
      handleChange({
        start: formatISODate(start),
        end: formatISODate(end),
        period: selectedPeriod
      });
    }
  };

  const startDate = useMemo(() => {
    if (start) {
      return formatISODate(start);
    }
    const periodOption = period ? options.find(({ id }) => id === period) : null;
    return periodOption ? formatISODate(periodOption.item.start) : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, period, options]);

  const endDate = useMemo(() => {
    if (end) {
      return formatISODate(end);
    }
    const periodOption = period ? options.find(({ id }) => id === period) : null;
    return periodOption ? formatISODate(periodOption.item.end) : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end, period, options]);

  const letRestore = letFilterRestore(filter);

  return (
    <div style={{ position: "relative" }}>
      <InlineRangeCalendar
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        label={filter.title}
        required={required}
        selectedPeriod={period}
        handleChange={onHandleChange}
        letClear={!required}
        onRestore={
          letRestore
            ? () => handleChange((filter.defaultValue ?? filter.default_value) as PeriodRange | null)
            : undefined
        }
        options={options}
        {...inlineRangeCalendarProps}
      />
    </div>
  );
};
